import uuidv4 from "uuid/v4";
import { fn } from "util";
import {
  ProductRepository,
  StockOperationsRepository,
  RuntimeRepository
} from "core/repository";
import { Base2Presenter } from "core/presenter";
import Big from "big.js";

class InventoryModifyPresenter extends Base2Presenter {
  initialState() {
    return {
      loading: false,
      resultSet: [],
      operationType: "",
      note: "",
      modifications: {},
      errors: {
        operationType: ""
      },
      pagination: {
        perPage: 25,
        currentPage: 1,
        total: 0
      },
      order: {
        field: "name",
        direction: "ASC"
      },
      filter: {
        type: "all",
        code: "",
        name: ""
      }
    };
  }

  async onStart() {
    this.doLoadResults();
  }

  onGoBack() {
    this.navigateTo("app.registration.product");
  }

  onRequestModifyByXML() {
    this.navigateTo("app.registration.product.inventory.modify.xml");
  }

  async doLoadResults() {
    this.enqueue({ loading: true });

    const productResults = await ProductRepository.getListOnline({
      order: [this.viewModel.order],
      pagination: {
        page: this.viewModel.pagination.currentPage,
        perPage: this.viewModel.pagination.perPage
      },
      filters: await this.getFilter()
    });

    if (productResults.success) {
      this.enqueue(vm => ({
        resultSet: productResults.data,
        pagination: {
          ...vm.pagination,
          total: productResults.total,
          perPage: productResults.perPage
        }
      }));
    } else {
      this.sendNotification({
        type: "error",
        title: "Ocorreu um erro",
        content:
          "Não foi possível carregar os dados. Tente novamente mais tarde."
      });
    }

    this.enqueue({ loading: false });
  }

  async getFilter() {
    const { type, code, name } = this.viewModel.filter;
    const currentUser = await this.mRuntime.get(
      RuntimeRepository.props.currentUser
    );

    const repositoryFilters = [
      {
        field: "company_uuid",
        operation: "EQ",
        value: currentUser.company_uuid
      },
      { field: "deleted", operation: "EQ", value: "false" },
      { field: "type", operation: "EQ", value: "product" }
    ];

    if (!fn.isBlank(code)) {
      repositoryFilters.push({ field: "code", operation: "LIKE", value: code });
    }

    if (!fn.isBlank(name)) {
      repositoryFilters.push({ field: "name", operation: "LIKE", value: name });
    }

    if (type !== "all") {
      const operation = { positive: "GT", zero: "EQ", negative: "LT" }[type];
      repositoryFilters.push({ field: "stock_count", operation, value: "0" });
    }

    return repositoryFilters;
  }

  onFilterChange(filter) {
    this.enqueue(
      vm => ({
        filter,
        pagination: {
          ...vm.pagination,
          currentPage: 1
        }
      }),
      () => this.doLoadResults()
    );
  }

  onPageChange(currentPage) {
    this.enqueue(
      vm => ({
        pagination: {
          ...vm.pagination,
          currentPage
        }
      }),
      () => this.doLoadResults()
    );
  }

  onOrderChange(field, direction) {
    this.enqueue(
      vm => ({
        order: {
          field,
          direction
        },
        pagination: {
          ...vm.pagination,
          currentPage: 1
        }
      }),
      () => this.doLoadResults()
    );
  }

  onChangeOperation(field, value) {
    this.enqueue({ [field]: value });
  }

  async onStockModify() {
    this.enqueue({ loading: true });

    const { operationType, modifications } = this.viewModel;

    if (operationType !== "in" && operationType !== "out") {
      this.enqueue(vm => ({
        errors: {
          ...vm.errors,
          operationType: "Tipo de Operação é obrigatório."
        }
      }));

      this.sendNotification({
        type: "warning",
        title: "Ocorreu um erro",
        content: "Preencha o tipo de operação para continuar."
      });

      this.enqueue({ loading: false });

      return;
    }

    const isModificationEmpty = Object.keys(modifications).length === 0;

    if (isModificationEmpty) {
      this.enqueue({ loading: false });

      this.sendNotification({
        type: "warning",
        title: "Ocorreu um erro",
        content:
          "Preencha a modificação de estoque de ao menos um produto para continuar."
      });

      return;
    }

    const invalidModifications = this.hasInvalidModifications();
    if (invalidModifications) {
      this.enqueue({ loading: false });

      this.sendNotification({
        type: "error",
        title: "Ocorreu um erro",
        content:
          "Verifique se os produtos que deseja modificar o estoque estão com ambos preço de compra e quantidade preenchidos."
      });

      return;
    }

    const success = await this.createStockOperations();

    if (success) {
      this.sendNotification({
        type: "success",
        content: "Todo o estoque foi alterado com sucesso."
      });

      this.navigateTo("app.registration.product");
    } else {
      this.sendNotification({
        type: "error",
        title: "Ocorreu um erro",
        content:
          "Tivemos um problema para salvar a modificação de estoque.\nPara facilitar, limpamos as movimentações que foram realizadas com sucesso."
      });
    }

    this.enqueue({ loading: false });
  }

  hasInvalidModifications() {
    const { operationType, modifications } = this.viewModel;

    return !!Object.keys(modifications).find(sellable_uuid => {
      const { quantity, costValue } = modifications[sellable_uuid];
      const isQuantityEmpty = quantity === "";
      const isCostValueEmpty = !costValue || Big(costValue).eq(0);

      return operationType === "in" && isQuantityEmpty && !isCostValueEmpty;
    });
  }

  async createStockOperations() {
    const { operationType, note, modifications } = this.viewModel;
    const activity = operationType === "in" ? "manual_input" : "manual_output";
    const finalNote = note.length === 0 ? "-" : note;

    for (let sellable_uuid of Object.keys(modifications)) {
      const { quantity, costValue } = modifications[sellable_uuid];
      const isQuantityEmpty = !quantity;
      const isCostValueEmpty = !costValue || Big(costValue).eq(0);

      if ((operationType === "out" && isQuantityEmpty) || isQuantityEmpty) {
        continue;
      }

      const shouldIncludeCostValue =
        operationType === "in" && !isCostValueEmpty;
      const addOptions = shouldIncludeCostValue
        ? { cost_value: parseInt(costValue, 10) }
        : {};
      const result = await StockOperationsRepository.create({
        activity,
        sellable_uuid,
        uuid: uuidv4(),
        type: operationType,
        note: finalNote,
        amount: parseFloat(quantity),
        ...addOptions
      });

      if (result.success) {
        delete modifications.sellable_uuid;
      } else {
        return false;
      }
    }

    return true;
  }

  onChangeField(set, productUuid, value, isFloat) {
    this.enqueue(vm => ({
      modifications: {
        ...vm.modifications,
        [productUuid]: {
          ...vm.modifications[productUuid],
          [set]: isFloat
            ? value.replace(/([^0-9/.])/g, "")
            : value.replace(/([^0-9])/g, "")
        }
      }
    }));
  }
}

export default InventoryModifyPresenter;



// WEBPACK FOOTER //
// ./src/components/app/registration/product/inventory/modify/presenter.js